import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Crown, Users, BookOpen, Lightbulb, FileBox, MessageCircle, Send,
  Plus, Trash2, Pencil, Search, ChevronRight, X, RefreshCw, Eye, EyeOff
} from "lucide-react";
import { toast } from "sonner";
import "../portfolio/styles.css";

const API = process.env.REACT_APP_BACKEND_URL;

async function api(method, path, body) {
  const opts = { method, headers: { "Content-Type": "application/json" }, credentials: "include" };
  if (body) opts.body = JSON.stringify(body);
  const r = await fetch(`${API}/api/admin/premium${path}`, opts);
  if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || `Error ${r.status}`); }
  return r.json();
}

const btnPrimary = { background: "linear-gradient(135deg, #262016 0%, #3B301F 100%)", border: "1px solid #B5793F" };
const inputCls = "w-full rounded-xl border border-[#26201625] bg-white/80 px-3.5 py-2.5 text-sm text-[#262016] outline-none focus:border-[#B5793F] placeholder-[#8A7E6B]";
const labelCls = "block text-[11px] uppercase tracking-[0.12em] text-[#8A7E6B] font-medium mb-1.5";

const TABS = [
  { id: "members", label: "Members", icon: Users },
  { id: "content", label: "Content", icon: BookOpen },
  { id: "templates", label: "Templates", icon: FileBox },
  { id: "messages", label: "Messages", icon: MessageCircle },
];

// ═══ Members Tab ═══
function MembersTab() {
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [allUsers, setAllUsers] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [duration, setDuration] = useState(90);

  const loadMembers = useCallback(() => { api("GET", "/members").then(setMembers).catch(() => {}); }, []);
  useEffect(() => { loadMembers(); }, [loadMembers]);

  const searchUsers = async () => {
    if (!searchQ.trim()) return;
    try {
      const d = await api("GET", `/all-members?search=${encodeURIComponent(searchQ)}`);
      setAllUsers(d);
    } catch (e) { toast.error(e.message); }
  };

  const upgrade = async (userId) => {
    try {
      await api("POST", `/upgrade/${userId}`, { duration_days: duration });
      toast.success("Upgraded to premium!");
      loadMembers();
      setAllUsers([]);
      setShowSearch(false);
      setSearchQ("");
    } catch (e) { toast.error(e.message); }
  };

  const downgrade = async (userId) => {
    if (!window.confirm("Remove premium access for this member?")) return;
    try {
      await api("POST", `/downgrade/${userId}`, {});
      toast.success("Downgraded to free");
      loadMembers();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div data-testid="admin-premium-members">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <button onClick={() => setShowSearch(!showSearch)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-[#F6F1E5]" style={btnPrimary} data-testid="add-premium-member-btn">
          <Plus size={14} /> Add Premium Member
        </button>
        <p className="text-sm text-[#8A7E6B]">{members.length} premium member{members.length !== 1 ? "s" : ""}</p>
      </div>

      {showSearch && (
        <div className="bg-white rounded-2xl p-5 border border-[#d4cbb8] mb-6" data-testid="premium-member-search">
          <div className="flex gap-3 mb-3">
            <input value={searchQ} onChange={(e) => setSearchQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchUsers()}
                   className={inputCls + " flex-1"} placeholder="Search by name or email..." data-testid="premium-search-input" />
            <button onClick={searchUsers} className="px-5 py-2.5 rounded-full text-sm font-semibold text-[#F6F1E5]" style={btnPrimary}>
              <Search size={14} />
            </button>
          </div>
          <div className="flex items-center gap-3 mb-3">
            <label className={labelCls + " mb-0"}>Duration (days):</label>
            <input type="number" value={duration} onChange={(e) => setDuration(parseInt(e.target.value) || 90)} min={1}
                   className="w-24 rounded-lg border border-[#26201625] px-3 py-1.5 text-sm text-[#262016]" data-testid="premium-duration-input" />
          </div>
          {allUsers.length > 0 && (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {allUsers.map((u) => (
                <div key={u.user_id} className="flex items-center gap-3 p-3 rounded-xl border border-[#26201610] hover:bg-[#FDFBF6]" data-testid={`user-search-${u.user_id}`}>
                  {u.picture ? <img src={u.picture} alt="" className="w-8 h-8 rounded-full" /> : <div className="w-8 h-8 rounded-full bg-[#EDE7D9] flex items-center justify-center text-xs font-bold text-[#5C5042]">{(u.name || u.email)[0]?.toUpperCase()}</div>}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#262016] truncate">{u.name || "—"}</p>
                    <p className="text-xs text-[#8A7E6B] truncate">{u.email}</p>
                  </div>
                  {u.tier === "premium" ? (
                    <span className="px-3 py-1 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700">Already Premium</span>
                  ) : (
                    <button onClick={() => upgrade(u.user_id)} className="px-4 py-1.5 rounded-full text-xs font-semibold text-[#F6F1E5]" style={btnPrimary} data-testid={`upgrade-${u.user_id}`}>
                      <Crown size={12} className="inline mr-1" /> Upgrade
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {members.length === 0 ? (
        <div className="bg-white/55 rounded-2xl p-8 text-center border border-[#d4cbb8]">
          <Crown size={22} className="mx-auto mb-3 text-[#8A7E6B]" />
          <p className="text-sm text-[#8A7E6B]">No premium members yet</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#d4cbb8] overflow-x-auto" data-testid="premium-members-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#d4cbb8] text-[11px] uppercase tracking-[0.12em] text-[#8A7E6B] font-medium">
                <th className="text-left px-4 py-3">Member</th>
                <th className="text-left px-4 py-3">Since</th>
                <th className="text-left px-4 py-3">Expires</th>
                <th className="text-left px-4 py-3">Messages</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.user_id} className="border-b border-[#26201610] hover:bg-[#FDFBF6]" data-testid={`premium-member-${m.user_id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      {m.picture ? <img src={m.picture} alt="" className="w-7 h-7 rounded-full" /> : <div className="w-7 h-7 rounded-full bg-[#EDE7D9] flex items-center justify-center text-[10px] font-bold text-[#5C5042]">{(m.name || m.email)[0]?.toUpperCase()}</div>}
                      <div><p className="font-medium text-[#262016]">{m.name}</p><p className="text-[11px] text-[#8A7E6B]">{m.email}</p></div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[#8A7E6B] text-xs">{m.premium_since ? new Date(m.premium_since).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3 text-xs">{m.premium_expires_at ? <span className={new Date(m.premium_expires_at) < new Date() ? "text-red-600" : "text-[#8A7E6B]"}>{new Date(m.premium_expires_at).toLocaleDateString()}</span> : "—"}</td>
                  <td className="px-4 py-3 text-xs">{m.unread_messages > 0 ? <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-600 font-semibold">{m.unread_messages} unread</span> : <span className="text-[#8A7E6B]">—</span>}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => downgrade(m.user_id)} className="px-3 py-1 rounded-full text-[11px] font-semibold border border-red-200 text-red-600 hover:bg-red-50" data-testid={`downgrade-${m.user_id}`}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══ Content Tab ═══
function ContentTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", body: "", media_url: "", type: "tutorial", order: 0, published: true });

  const load = useCallback(() => {
    const q = filter ? `?content_type=${filter}` : "";
    api("GET", `/content${q}`).then(setItems).catch(() => setItems([]));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    try {
      if (editing) {
        await api("PUT", `/content/${editing}`, form);
        toast.success("Updated");
      } else {
        await api("POST", "/content", form);
        toast.success("Created");
      }
      setForm({ title: "", description: "", body: "", media_url: "", type: "tutorial", order: 0, published: true });
      setEditing(null);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const edit = (item) => {
    setEditing(item.id);
    setForm({ title: item.title, description: item.description, body: item.body, media_url: item.media_url, type: item.type, order: item.order, published: item.published });
  };

  const del = async (id) => {
    if (!window.confirm("Delete this content?")) return;
    try { await api("DELETE", `/content/${id}`); load(); } catch (e) { toast.error(e.message); }
  };

  return (
    <div data-testid="admin-premium-content">
      <div className="flex flex-wrap gap-2 mb-5">
        {["", "tutorial", "tip", "update"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} className="px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide transition-all"
                  style={{ background: f === filter ? "#262016" : "transparent", color: f === filter ? "#EDE7D9" : "#5C5042", border: `1px solid ${f === filter ? "#262016" : "rgba(38,32,22,0.2)"}` }}>
            {f || "All"}
          </button>
        ))}
      </div>

      {/* Form */}
      <div className="bg-white rounded-2xl p-5 border border-[#d4cbb8] mb-6" data-testid="content-form">
        <h3 className="text-sm font-semibold text-[#262016] mb-4">{editing ? "Edit Content" : "Add Premium Content"}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
          <div>
            <label className={labelCls}>Title</label>
            <input value={form.title} onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))} className={inputCls} placeholder="Tutorial title..." data-testid="content-title" />
          </div>
          <div>
            <label className={labelCls}>Type</label>
            <select value={form.type} onChange={(e) => setForm(p => ({ ...p, type: e.target.value }))} className={inputCls} data-testid="content-type">
              <option value="tutorial">Tutorial</option>
              <option value="tip">Tip / Insight</option>
              <option value="update">Exclusive Update</option>
            </select>
          </div>
        </div>
        <div className="mb-3">
          <label className={labelCls}>Description</label>
          <input value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} className={inputCls} placeholder="Brief description..." data-testid="content-desc" />
        </div>
        <div className="mb-3">
          <label className={labelCls}>Body</label>
          <textarea value={form.body} onChange={(e) => setForm(p => ({ ...p, body: e.target.value }))} className={inputCls + " h-32 resize-none"} placeholder="Full content..." data-testid="content-body" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          <div>
            <label className={labelCls}>Media URL (YouTube, etc.)</label>
            <input value={form.media_url} onChange={(e) => setForm(p => ({ ...p, media_url: e.target.value }))} className={inputCls} placeholder="https://..." data-testid="content-media" />
          </div>
          <div>
            <label className={labelCls}>Order</label>
            <input type="number" value={form.order} onChange={(e) => setForm(p => ({ ...p, order: parseInt(e.target.value) || 0 }))} className={inputCls} data-testid="content-order" />
          </div>
          <div className="flex items-end gap-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.published} onChange={(e) => setForm(p => ({ ...p, published: e.target.checked }))} data-testid="content-published" />
              <span className="text-sm text-[#5C5042]">Published</span>
            </label>
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={save} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-[#F6F1E5]" style={btnPrimary} data-testid="content-save-btn">
            {editing ? "Update" : "Create"}
          </button>
          {editing && <button onClick={() => { setEditing(null); setForm({ title: "", description: "", body: "", media_url: "", type: "tutorial", order: 0, published: true }); }} className="text-sm text-[#8A7E6B]">Cancel</button>}
        </div>
      </div>

      {/* List */}
      {items.map((item) => (
        <div key={item.id} className="bg-white rounded-2xl flex items-center gap-3 px-5 py-4 border border-[#d4cbb8]" style={{ marginBottom: "20px" }} data-testid={`content-item-${item.id}`}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#EDE7D9] text-[#B5793F] uppercase">{item.type}</span>
              {!item.published && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-500">Draft</span>}
            </div>
            <p className="text-sm font-medium text-[#262016] truncate">{item.title}</p>
            {item.description && <p className="text-xs text-[#8A7E6B] truncate">{item.description}</p>}
          </div>
          <button onClick={() => edit(item)} className="w-8 h-8 rounded-full border border-[#26201620] flex items-center justify-center text-[#8A7E6B] hover:text-[#262016]" data-testid={`content-edit-${item.id}`}>
            <Pencil size={12} />
          </button>
          <button onClick={() => del(item.id)} className="w-8 h-8 rounded-full border border-[#26201620] flex items-center justify-center text-[#8A7E6B] hover:text-red-700" data-testid={`content-del-${item.id}`}>
            <Trash2 size={12} />
          </button>
        </div>
      ))}
      {items.length === 0 && <p className="text-sm text-[#8A7E6B] text-center py-4">No content yet — create your first premium tutorial above.</p>}
    </div>
  );
}

// ═══ Templates Tab ═══
function TemplatesTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", category: "", file_url: "", preview_url: "", file_type: "" });

  const load = useCallback(() => { api("GET", "/templates").then(setItems).catch(() => setItems([])); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    try {
      await api("POST", "/templates", form);
      toast.success("Template added");
      setForm({ title: "", description: "", category: "", file_url: "", preview_url: "", file_type: "" });
      load();
    } catch (e) { toast.error(e.message); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try { await api("DELETE", `/templates/${id}`); load(); } catch (e) { toast.error(e.message); }
  };

  return (
    <div data-testid="admin-premium-templates">
      <div className="bg-white rounded-2xl p-5 border border-[#d4cbb8] mb-6" data-testid="template-form">
        <h3 className="text-sm font-semibold text-[#262016] mb-4">Add Template</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
          <div><label className={labelCls}>Title</label><input value={form.title} onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))} className={inputCls} placeholder="Template name..." data-testid="template-title" /></div>
          <div><label className={labelCls}>Category</label><input value={form.category} onChange={(e) => setForm(p => ({ ...p, category: e.target.value }))} className={inputCls} placeholder="e.g., Social Media, Presentations" data-testid="template-category" /></div>
        </div>
        <div className="mb-3"><label className={labelCls}>Description</label><input value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} className={inputCls} placeholder="Brief description..." data-testid="template-desc" /></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div><label className={labelCls}>File URL</label><input value={form.file_url} onChange={(e) => setForm(p => ({ ...p, file_url: e.target.value }))} className={inputCls} placeholder="https://..." data-testid="template-file" /></div>
          <div><label className={labelCls}>File Type</label><input value={form.file_type} onChange={(e) => setForm(p => ({ ...p, file_type: e.target.value }))} className={inputCls} placeholder="e.g., PSD, AI, Figma, PDF" data-testid="template-filetype" /></div>
        </div>
        <button onClick={save} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-[#F6F1E5]" style={btnPrimary} data-testid="template-save-btn">
          <Plus size={14} /> Add Template
        </button>
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-[#8A7E6B] text-center py-4">No templates yet.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((t) => (
            <div key={t.id} className="bg-white rounded-2xl p-5 border border-[#d4cbb8] relative" data-testid={`template-item-${t.id}`}>
              <button onClick={() => del(t.id)} className="absolute top-3 right-3 w-7 h-7 rounded-full border border-[#26201620] flex items-center justify-center text-[#8A7E6B] hover:text-red-700" data-testid={`template-del-${t.id}`}>
                <Trash2 size={11} />
              </button>
              <FileBox size={18} className="text-[#B5793F] mb-2" />
              <p className="text-sm font-medium text-[#262016]">{t.title}</p>
              {t.category && <p className="text-[10px] uppercase tracking-wider text-[#B5793F] font-semibold">{t.category}</p>}
              {t.description && <p className="text-xs text-[#8A7E6B] mt-1">{t.description}</p>}
              {t.file_type && <p className="text-[10px] text-[#8A7E6B] mt-1">{t.file_type}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══ Messages Tab ═══
function MessagesTab() {
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const loadThreads = useCallback(() => { api("GET", "/threads").then(setThreads).catch(() => {}); }, []);
  useEffect(() => { loadThreads(); }, [loadThreads]);

  const openThread = async (userId) => {
    setActiveThread(userId);
    try {
      const msgs = await api("GET", `/messages/${userId}`);
      setMessages(msgs);
      loadThreads();
    } catch (e) { toast.error(e.message); }
  };

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim() || !activeThread) return;
    setSending(true);
    try {
      await api("POST", `/messages/${activeThread}`, { text: reply.trim() });
      setReply("");
      const msgs = await api("GET", `/messages/${activeThread}`);
      setMessages(msgs);
      loadThreads();
    } catch (err) { toast.error(err.message); }
    setSending(false);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="admin-premium-messages">
      {/* Thread List */}
      <div className="md:col-span-1 bg-white rounded-2xl border border-[#d4cbb8] overflow-hidden" style={{ maxHeight: "600px" }}>
        <div className="px-4 py-3 border-b border-[#d4cbb8]">
          <p className="text-xs font-semibold text-[#8A7E6B] uppercase tracking-wider">Conversations</p>
        </div>
        <div className="overflow-y-auto" style={{ maxHeight: "550px" }}>
          {threads.length === 0 && <p className="p-4 text-sm text-[#8A7E6B]">No conversations yet</p>}
          {threads.map((t) => (
            <button key={t.user_id} onClick={() => openThread(t.user_id)}
                    className={`w-full text-left px-4 py-3 border-b border-[#26201608] hover:bg-[#FDFBF6] transition-colors ${activeThread === t.user_id ? "bg-[#FDFBF6]" : ""}`}
                    data-testid={`thread-${t.user_id}`}>
              <div className="flex items-center gap-2.5">
                {t.picture ? <img src={t.picture} alt="" className="w-8 h-8 rounded-full" /> : <div className="w-8 h-8 rounded-full bg-[#EDE7D9] flex items-center justify-center text-[10px] font-bold text-[#5C5042]">{(t.name || t.email)[0]?.toUpperCase()}</div>}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#262016] truncate">{t.name || t.email}</p>
                  <p className="text-[11px] text-[#8A7E6B] truncate">{t.last_message}</p>
                </div>
                {t.unread > 0 && <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white">{t.unread}</span>}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Messages */}
      <div className="md:col-span-2 bg-white rounded-2xl border border-[#d4cbb8] overflow-hidden flex flex-col" style={{ height: "600px" }}>
        {!activeThread ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageCircle size={28} className="mx-auto mb-3 text-[#8A7E6B]" />
              <p className="text-sm text-[#8A7E6B]">Select a conversation to view messages</p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="admin-chat-messages">
              {messages.map((msg) => (
                <div key={msg.id} className={`flex ${msg.sender === "admin" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] rounded-2xl px-4 py-3 ${msg.sender === "admin" ? "bg-[#262016] text-[#EDE7D9]" : "bg-[#F6F1E5] text-[#262016] border border-[#d4cbb8]"}`}>
                    <p className="text-sm whitespace-pre-line">{msg.text}</p>
                    <p className="text-[10px] mt-1 text-[#8A7E6B]">
                      {msg.sender_name} · {new Date(msg.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>
            <form onSubmit={sendReply} className="p-4 border-t border-[#d4cbb8] flex gap-3" data-testid="admin-reply-form">
              <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type your reply..."
                     className="flex-1 rounded-full border border-[#26201620] bg-white px-4 py-2.5 text-sm text-[#262016] outline-none focus:border-[#B5793F]"
                     data-testid="admin-reply-input" disabled={sending} />
              <button type="submit" disabled={sending || !reply.trim()} className="w-10 h-10 rounded-full flex items-center justify-center text-[#EDE7D9] disabled:opacity-40" style={{ background: "#262016" }} data-testid="admin-reply-send">
                <Send size={16} />
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

// ═══ Main Component ═══
export default function AdminPremium() {
  const [tab, setTab] = useState("members");
  const [stats, setStats] = useState(null);

  useEffect(() => { api("GET", "/stats").then(setStats).catch(() => {}); }, []);

  return (
    <div data-testid="admin-premium-page">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8" data-testid="premium-stats">
          <div className="bg-white rounded-2xl p-5 border border-[#d4cbb8]">
            <div className="flex items-center gap-2 text-[#B5793F] text-xs font-semibold uppercase tracking-wider mb-1"><Crown size={14} /> Premium</div>
            <p className="text-3xl font-light text-[#262016]">{stats.total_premium}</p>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-[#d4cbb8]">
            <div className="flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-wider mb-1"><BookOpen size={14} /> Content</div>
            <p className="text-3xl font-light text-[#262016]">{stats.total_content}</p>
            <p className="text-xs text-[#8A7E6B] mt-1">{stats.tutorials} tutorials · {stats.tips} tips</p>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-[#d4cbb8]">
            <div className="flex items-center gap-2 text-amber-600 text-xs font-semibold uppercase tracking-wider mb-1"><FileBox size={14} /> Templates</div>
            <p className="text-3xl font-light text-[#262016]">{stats.total_templates}</p>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-[#d4cbb8]">
            <div className="flex items-center gap-2 text-green-600 text-xs font-semibold uppercase tracking-wider mb-1"><MessageCircle size={14} /> Messages</div>
            <p className="text-3xl font-light text-[#262016]">{stats.total_messages}</p>
            {stats.unread_messages > 0 && <p className="text-xs text-red-500 mt-1">{stats.unread_messages} unread</p>}
          </div>
        </div>
      )}

      {/* Tabs */}
      <nav className="flex gap-2 mb-8" data-testid="premium-admin-tabs">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`premium-tab-${t.id}`}
                  className="px-5 py-2 rounded-full text-sm font-semibold uppercase tracking-wide transition-all flex items-center gap-2"
                  style={{ background: t.id === tab ? "#262016" : "transparent", color: t.id === tab ? "#EDE7D9" : "#5C5042", border: `1px solid ${t.id === tab ? "#262016" : "rgba(38,32,22,0.2)"}` }}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </nav>

      {tab === "members" && <MembersTab />}
      {tab === "content" && <ContentTab />}
      {tab === "templates" && <TemplatesTab />}
      {tab === "messages" && <MessagesTab />}
    </div>
  );
}
